import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { FaArrowLeft, FaCalendarAlt, FaTag, FaLayerGroup, FaMapMarkerAlt, FaAlignLeft, FaImage, FaHistory, FaCheckCircle, FaExchangeAlt, FaComment, FaInfoCircle, FaWhatsapp } from "react-icons/fa";
import styles from "./DetalleIncidencia.module.css";
import { useIsMobile } from "../hooks/useResponsive";
import { AppScreen, AppHeader, AppCard, SectionLabel, Badge, IconBox, AppSpinner, AppEmptyState } from "../components/app";
import { appColors, appType, appSpacing, appRadius } from "../utils/appTheme";

/**
 * Lo que el residente ve del historial: cambios de estado y respuestas al
 * cliente, nunca una nota interna (C4). Las notas nuevas van a
 * incidentNotesInternal (solo staff); las viejas quedaron en incidentHistory
 * con showToResident false, o con el texto solo en messageInternal. Además se
 * descarta el campo messageInternal de cada registro (respuestas mixtas
 * viejas), así no queda ni en el estado de la pantalla.
 */
export const historialParaResidente = (items) =>
  (items || [])
    .filter((h) => {
      if (h.action !== "comment_added") return true;
      if (h.showToResident === false) return false;
      const alResidente = h.messageClient || (h.messageInternal ? "" : h.message);
      return String(alResidente || "").trim() !== "";
    })
    .map(({ messageInternal, ...resto }) => resto);

const DetalleIncidencia = ({ incidencia, onBack, setSelected, goBack }) => {
  const isMobile = useIsMobile();
  const [incidentHistory, setIncidentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (incidencia?.id) {
      fetchIncidentHistory();
    }
  }, [incidencia?.id]);

  const fetchIncidentHistory = async () => {
    try {
      setLoadingHistory(true);
      const q = query(
        collection(db, "incidentHistory"),
        where("incidenciaId", "==", incidencia.id),
        orderBy("timestamp", "desc")
      );
      const querySnapshot = await getDocs(q);
      const historyData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setIncidentHistory(historialParaResidente(historyData));
    } catch (error) {
      console.error("Error al cargar historial:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  if (!incidencia) return null;

  // Ya filtrado al cargar (sin notas internas).
  const historyForResident = incidentHistory || [];

  const clientReplyText =
    incidencia.lastClientResponse || incidencia.lastResponse || "";

  // BUG-50 fix: Manejar Firestore Timestamps, Date objects y strings
  const formatDate = (dateVal) => {
    try {
      let date;
      if (!dateVal) return "—";
      if (dateVal.toDate) date = dateVal.toDate(); // Firestore Timestamp
      else if (dateVal.seconds) date = new Date(dateVal.seconds * 1000); // Plain timestamp object
      else if (dateVal instanceof Date) date = dateVal;
      else date = new Date(dateVal);
      if (isNaN(date.getTime())) return String(dateVal);
      return date.toLocaleDateString('es-UY', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return String(dateVal || "—");
    }
  };

  const formatHistoryDate = (date) => {
    if (!date) return "—";
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString("es-UY", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (error) {
      return "—";
    }
  };

  const getPrioridadStyle = (prioridad) => {
    const priority = (prioridad || "").toLowerCase();
    switch (priority) {
      case "alta":
        return styles.priorityHigh;
      case "media":
        return styles.priorityMedium;
      case "baja":
        return styles.priorityLow;
      default:
        return styles.priorityMedium;
    }
  };

  const getEstadoStyle = (estado) => {
    const status = (estado || "").toLowerCase();
    switch (status) {
      case "reportada":
      case "reportado":
        return styles.statusReported;
      case "pendiente":
        return styles.statusPending;
      case "en proceso":
      case "en curso":
        return styles.statusInProgress;
      case "resuelta":
      case "resuelto":
        return styles.statusResolved;
      default:
        return styles.statusDefault;
    }
  };

  // ─── Versión mobile: estilo app "Radial" ────────────────────────────────────
  if (isMobile) {
    const statusText = incidencia.status || incidencia.state || "";
    const priorityText = incidencia.priority || incidencia.prioridad || "";
    const statusTone = (() => {
      const s = statusText.toLowerCase();
      if (s.includes("resuel")) return "green";
      if (s.includes("proceso") || s.includes("curso") || s.includes("pend")) return "amber";
      if (s.includes("report")) return "blue";
      return "ink3";
    })();

    const timelineItems = [
      {
        id: "creation",
        action: "creation",
        date: incidencia.date,
        description: "Incidencia reportada por el usuario",
        title: "Se reportó al proveedor",
        status: "Reportada",
      },
      ...historyForResident.map((item) => ({
        ...item,
        title:
          item.action === "status_change"
            ? "Cambio de Estado"
            : "Respuesta del administrador",
      })),
    ].sort((a, b) => {
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return dateB - dateA;
    });

    // Tono de estado para el badge (deriva del estado de la incidencia)
    const statusToneKey = (() => {
      switch (statusTone) {
        case "green":
          return "green";
        case "amber":
          return "amber";
        case "red":
          return "red";
        case "ink3":
          return "ink3";
        default:
          return "blue";
      }
    })();

    // Datos para el footer estructurado en grilla (FECHA / TIPO / TEMA / UBICACIÓN)
    const footerData = [
      { label: "Fecha", value: formatDate(incidencia.date), tabular: true },
      incidencia.type ? { label: "Tipo", value: incidencia.type } : null,
      incidencia.issue ? { label: "Tema", value: incidencia.issue } : null,
      incidencia.ubication
        ? { label: "Ubicación", value: incidencia.ubication }
        : null,
    ].filter(Boolean);

    return (
      <AppScreen
        header={
          <AppHeader
            title={incidencia.title}
            subtitle="Detalle de incidencia"
            onBack={onBack || goBack}
          />
        }
      >
        {/* Estado y prioridad — info que el encabezado no muestra */}
        {(statusText || priorityText) && (
          <AppCard style={{ marginBottom: appSpacing.section }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {statusText && (
                <Badge tone={statusToneKey} dot>
                  {statusText}
                </Badge>
              )}
              {priorityText && (
                <Badge tone="ink3">Prioridad {priorityText.toLowerCase()}</Badge>
              )}
            </div>
          </AppCard>
        )}

        {/* Datos: footer estructurado en grilla (label uppercase + valor debajo) */}
        {footerData.length > 0 && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>Datos</SectionLabel>
            <AppCard style={{ marginBottom: appSpacing.section }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                }}
              >
                {footerData.map((r) => (
                  <div key={r.label} style={{ minWidth: 0 }}>
                    <div
                      style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}
                    >
                      {r.label}
                    </div>
                    <div
                      style={{
                        ...appType.body,
                        fontSize: 13.5,
                        overflowWrap: "anywhere",
                        ...(r.tabular ? { fontVariantNumeric: "tabular-nums" } : null),
                      }}
                    >
                      {r.value}
                    </div>
                  </div>
                ))}
              </div>
            </AppCard>
          </>
        )}

        {/* Descripción */}
        {incidencia.description && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>Descripción</SectionLabel>
            <AppCard style={{ marginBottom: appSpacing.section }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <IconBox icon={<FaAlignLeft />} size={36} />
                <p
                  style={{
                    ...appType.body,
                    margin: 0,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {incidencia.description}
                </p>
              </div>
            </AppCard>
          </>
        )}

        {/* Imagen adjunta */}
        {incidencia.imageUrl && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>Imagen adjunta</SectionLabel>
            <AppCard padding={0} style={{ marginBottom: appSpacing.section, overflow: "hidden" }}>
              <img
                src={incidencia.imageUrl}
                alt="Imagen de la incidencia"
                style={{ display: "block", width: "100%", height: "auto", maxWidth: "100%" }}
              />
            </AppCard>
          </>
        )}

        {/* Respuesta de la administración — destacada en caja fría cream */}
        {clientReplyText ? (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>
              Respuesta de la administración
            </SectionLabel>
            <AppCard
              accent="blue"
              style={{
                marginBottom: appSpacing.section,
                borderLeftColor: appColors.green,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <IconBox icon={<FaWhatsapp />} color={appColors.green} size={36} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ ...appType.statLabel, color: appColors.green }}>
                    Administración
                  </div>
                  {(incidencia.lastClientResponseDate || incidencia.lastResponseDate) && (
                    <div
                      style={{
                        ...appType.caption,
                        marginTop: 2,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatHistoryDate(
                        incidencia.lastClientResponseDate || incidencia.lastResponseDate
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div
                style={{
                  background: appColors.cream,
                  borderRadius: appRadius.tile,
                  padding: "12px 14px",
                }}
              >
                <p
                  style={{
                    ...appType.body,
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {clientReplyText}
                </p>
              </div>
            </AppCard>
          </>
        ) : null}

        {/* Historial */}
        <SectionLabel style={{ marginBottom: 12 }}>Historial de actividades</SectionLabel>
        {loadingHistory ? (
          <AppCard>
            <AppSpinner />
          </AppCard>
        ) : incidentHistory.length === 0 ? (
          <AppCard padding={0}>
            <AppEmptyState
              icon={<FaHistory />}
              title="Sin actividades aún"
              description="Cuando la administración actualice el estado o responda, verás el historial acá."
            />
          </AppCard>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {timelineItems.map((item) => {
              const isCreation = item.action === "creation";
              const isStatus = item.action === "status_change";
              const accentColor = isCreation
                ? appColors.green
                : isStatus
                ? appColors.amber
                : appColors.blue;
              const message =
                item.action === "comment_added"
                  ? (item.messageClient || item.message || "").trim() || "—"
                  : null;
              return (
                <AppCard
                  key={item.id}
                  padding={0}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{ padding: "16px 16px 0" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 10,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          minWidth: 0,
                        }}
                      >
                        <IconBox
                          icon={
                            isCreation ? (
                              <FaCheckCircle />
                            ) : isStatus ? (
                              <FaExchangeAlt />
                            ) : (
                              <FaComment />
                            )
                          }
                          color={accentColor}
                          size={36}
                        />
                        <span
                          style={{
                            ...appType.body,
                            fontSize: 14,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {item.title}
                        </span>
                      </div>
                      <span
                        style={{
                          ...appType.caption,
                          flexShrink: 0,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatHistoryDate(item.date)}
                      </span>
                    </div>

                    {message != null ? (
                      <p
                        style={{
                          ...appType.bodySm,
                          color: appColors.ink,
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {message}
                      </p>
                    ) : (
                      <>
                        {!isStatus && item.description && (
                          <p
                            style={{
                              ...appType.bodySm,
                              margin: 0,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {item.description}
                          </p>
                        )}

                        {isCreation && item.status && (
                          <div style={{ marginTop: 8 }}>
                            <Badge tone="blue" dot>
                              {item.status}
                            </Badge>
                          </div>
                        )}

                        {isStatus && item.oldValue && item.newValue && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                              marginTop: 8,
                            }}
                          >
                            <Badge tone="ink3">{item.oldValue}</Badge>
                            <span style={{ color: appColors.ink3 }}>→</span>
                            <Badge tone="amber">{item.newValue}</Badge>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Línea de estado coloreada abajo del tile del timeline */}
                  <div
                    style={{
                      height: 3,
                      background: accentColor,
                      marginTop: 16,
                    }}
                  />
                </AppCard>
              );
            })}
          </div>
        )}
      </AppScreen>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.detailCard}>
        {/* Header Section */}
        <div className={styles.header}>
          <button onClick={onBack} className={styles.backButton}>
            <FaArrowLeft className={styles.backIcon} />
            Volver
          </button>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{incidencia.title}</h1>
            <div className={styles.badgesSection}>
              <span className={`${styles.statusBadge} ${getEstadoStyle(incidencia.status || incidencia.state)}`}>
                {incidencia.status || incidencia.state}
              </span>
              <span className={`${styles.priorityBadge} ${getPrioridadStyle(incidencia.priority || incidencia.prioridad)}`}>
                {incidencia.priority || incidencia.prioridad}
              </span>
            </div>
          </div>
        </div>

        {/* Compact Info Grid */}
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <FaCalendarAlt className={styles.infoIcon} />
            <span className={styles.infoLabel}>Fecha:</span>
            <span className={styles.infoValue}>{formatDate(incidencia.date)}</span>
          </div>
          <div className={styles.infoItem}>
            <FaTag className={styles.infoIcon} />
            <span className={styles.infoLabel}>Tipo:</span>
            <span className={styles.infoValue}>{incidencia.type}</span>
          </div>
          <div className={styles.infoItem}>
            <FaLayerGroup className={styles.infoIcon} />
            <span className={styles.infoLabel}>Tema:</span>
            <span className={styles.infoValue}>{incidencia.issue}</span>
          </div>
          {incidencia.ubication && (
            <div className={styles.infoItem}>
              <FaMapMarkerAlt className={styles.infoIcon} />
              <span className={styles.infoLabel}>Ubicación:</span>
              <span className={styles.infoValue}>{incidencia.ubication}</span>
            </div>
          )}
        </div>

        {/* Description Section */}
        <div className={styles.descriptionSection}>
          <div className={styles.sectionHeader}>
            <FaAlignLeft className={styles.sectionIcon} />
            <h2 className={styles.sectionTitle}>Descripción</h2>
          </div>
          <p className={styles.description}>{incidencia.description}</p>
        </div>

        {/* Image Section */}
        {incidencia.imageUrl && (
          <div className={styles.imageSection}>
            <div className={styles.sectionHeader}>
              <FaImage className={styles.sectionIcon} />
              <h2 className={styles.sectionTitle}>Imagen Adjunta</h2>
            </div>
            <div className={styles.imageContainer}>
              <img
                src={incidencia.imageUrl}
                alt="Imagen de la incidencia"
                className={styles.image}
              />
            </div>
          </div>
        )}

        {clientReplyText ? (
          <div className={styles.descriptionSection}>
            <div className={styles.sectionHeader}>
              <FaWhatsapp className={styles.sectionIcon} style={{ color: "#25D366" }} />
              <h2 className={styles.sectionTitle}>Respuesta de la administración</h2>
            </div>
            <p className={styles.description} style={{ whiteSpace: "pre-wrap" }}>
              {clientReplyText}
            </p>
            {(incidencia.lastClientResponseDate || incidencia.lastResponseDate) && (
              <p style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
                {formatHistoryDate(incidencia.lastClientResponseDate || incidencia.lastResponseDate)}
              </p>
            )}
          </div>
        ) : null}

        {/* History Section */}
        <div className={styles.historySection}>
          <div className={styles.sectionHeader}>
            <FaHistory className={styles.sectionIcon} />
            <h2 className={styles.sectionTitle}>Historial de Actividades</h2>
          </div>
          
          {loadingHistory ? (
            <div className={styles.loadingHistory}>
              <div className={styles.loadingSpinner}></div>
              <span>Cargando historial...</span>
            </div>
          ) : incidentHistory.length === 0 ? (
            <div className={styles.noHistory}>
              <FaInfoCircle className={styles.noHistoryIcon} />
              <p className={styles.noHistoryText}>
                No hay actividades registradas para esta incidencia.
              </p>
            </div>
          ) : (
            <div className={styles.historyList}>
              {/* Combinar creación + historial y ordenar por fecha descendente */}
              {[
                // Creación de la incidencia
                {
                  id: 'creation',
                  action: 'creation',
                  date: incidencia.date,
                  description: 'Incidencia reportada por el usuario',
                  title: 'Se reportó al proveedor',
                  status: 'Reportada'
                },
                // Historial de cambios (sin notas internas)
                ...historyForResident.map(item => ({
                  ...item,
                  title: item.action === "status_change" 
                    ? "Cambio de Estado" 
                    : "Respuesta del administrador"
                }))
              ]
              .sort((a, b) => {
                // Ordenar por fecha descendente (más reciente primero)
                const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
                const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
                return dateB - dateA;
              })
              .map((item) => (
                <div 
                  key={item.id} 
                  className={`${styles.historyItem} ${
                    item.action === "creation" 
                      ? styles.historyItemCreated
                      : item.action === "status_change" 
                        ? styles.historyItemStatus 
                        : styles.historyItemComment
                  }`}
                >
                  <div className={styles.historyItemHeader}>
                    <div className={styles.historyItemLeft}>
                      <span className={styles.historyItemIcon}>
                        {item.action === "creation" ? <FaCheckCircle /> : 
                         item.action === "status_change" ? <FaExchangeAlt /> : <FaComment />}
                      </span>
                      <span className={styles.historyItemTitle}>{item.title}</span>
                    </div>
                    <span className={styles.historyItemDate}>
                      {formatHistoryDate(item.date)}
                    </span>
                  </div>
                  
                  <div className={styles.historyItemContent}>
                    {item.action === "comment_added" ? (
                      <div className={styles.historyItemMessage}>
                        {(item.messageClient || item.message || "").trim() || "—"}
                      </div>
                    ) : (
                      <>
                        {item.action !== "status_change" && (
                          <div className={styles.historyItemDescription}>
                            {item.description}
                          </div>
                        )}
                        
                        {item.action === "creation" && item.status && (
                          <div className={styles.creationStatusContainer}>
                            <span className={`${styles.creationStatusBadge} ${getEstadoStyle(item.status)}`}>
                              {item.status}
                            </span>
                          </div>
                        )}
                        
                        {item.action === "status_change" && item.oldValue && item.newValue && (
                          <div className={styles.statusChangeContainer}>
                            <span className={styles.statusChangeOld}>{item.oldValue}</span>
                            <span className={styles.statusChangeArrow}>→</span>
                            <span className={styles.statusChangeNew}>{item.newValue}</span>
                          </div>
                        )}
                        
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetalleIncidencia;
